import type {
  MarkBox,
  PassageMark,
} from "@/features/material-compliance/components/declarations/review/studio-v2/document/markSelection"
import { PowerBar } from "@/features/material-compliance/components/declarations/review/studio/PowerBar"
import {
  textTerms,
  type FilterToken,
} from "@/features/material-compliance/components/declarations/review/studio/TableSearch"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { pdfWorkerUrl } from "@/lib/pdfjsWorker"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react"
import type { CSSProperties } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"
import "./docHighlights.css"
import {
  buildTextIndex,
  clearNamedHighlight,
  findAllMatches,
  findQuote,
  mergeRectsIntoLines,
  rangeFromMatch,
  scrollElementIntoView,
  scrollRangeIntoView,
  scrollWithoutMovingThePage,
  setNamedHighlight,
  type Match,
  type TextIndex,
} from "./highlights"
import { pageAspectRatio } from "./pageAspect"

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

/** Palette size, pairs with --src-c0…7 and the ::highlight rules in docHighlights.css. */
export const SOURCE_COLOR_COUNT = 8

export function sourceColorVar(colorIndex: number): string {
  return `var(--src-c${colorIndex % SOURCE_COLOR_COUNT})`
}

export interface Highlight {
  page: number
  /** Null for a proof with no quotable text (a region): the jump lands on the
   *  PAGE and paints nothing, which is the honest maximum for a box. */
  quote: string | null
  /** WHICH copy of `quote` on the page to jump to, 1-based. Absent ⇒ the first,
   *  see `SourceRegion.occurrence`. */
  occurrence?: number
  /**
   * THE SOURCE THIS JUMP NAMES, when the caller already knows it, a card
   * source-line click, `advanceTo`, an `at=` deep link. Resolved FIRST, before
   * page+quote+occurrence: a citation click today re-derives its own source
   * from the very words it clicked, which is a needless round trip whenever the
   * key is already in hand. A key that names no CURRENT source (stale/foreign)
   * degrades to the tie-break below rather than naming no source, "coarser,
   * never missing", same contract as `jumpSourceKey`'s occurrence tie-break.
   * Absent for the callers that only ever had quote+page+occurrence to give
   * (detections, the issue-brief's fireLead), they resolve exactly as before.
   */
  key?: string
}

/**
 * One evidence quote belonging to a statement. It is painted as a persistent
 * text tint while its statement is the selected one, and rides the ◀▶ source
 * track. `statementId` is the covering statement, it drives the tint, the ruler
 * and the colour. `itemKey` is the per-step grouping unit the source track walks
 * by: step 1 groups by statement (the default, `itemKey` absent ⇒ `statementId`),
 * step 3 by the statement's articles. See `navItems`.
 */
/** One fallback highlight: a source the text layer could not place, with the
 *  backend's page coordinate. */
/**
 * ONE MERGED LINE OF THE "TEXT MARKER" OVERLAY (Studio V2's `overlayQuotes`
 * mode). Positioned in PIXELS against the `.doc-pdf-page` container the same
 * range would otherwise paint under (see `quoteOverlaysFromRange`), rather
 * than page fractions like `QuoteBox`: a text range's rects come from the
 * live text layer, not a backend bbox, so there is no reason to round-trip
 * through fractions. `colorIndex` absent ⇒ the anchor's fixed primary tint.
 */
interface QuoteOverlay {
  key: string
  kind: "anchor" | "src" | "hot"
  colorIndex?: number
  left: number
  top: number
  width: number
  height: number
}

/** No overlays for any page: a stable identity so `overlaysFor` (mirroring
 *  `boxesFor`) does not defeat `PdfPage`'s memo when overlays are simply off. */
const EMPTY_OVERLAYS = new Map<number, QuoteOverlay[]>()

/** Stable identity for the `marks` prop's default, same reasoning as
 *  `EMPTY_OVERLAYS` above: a fresh `[]` on every render would defeat
 *  `marksByPage`'s memo for every v1 caller that never passes marks. */
const EMPTY_MARKS: PassageMark[] = []

/** No marks for any page: mirrors `EMPTY_OVERLAYS`'s stable-identity contract
 *  for `marksFor`. */
const EMPTY_PAGE_MARKS = new Map<number, PassageMark[]>()

/**
 * CONVERT ONE MATCHED RANGE INTO THE TEXT-MARKER OVERLAY'S MERGED LINE RECTS.
 * `range.getClientRects()` is one rect per pdf.js text-layer span the range
 * crosses, the exact per-word choppiness `overlayQuotes` exists to hide, so
 * it goes straight through `mergeRectsIntoLines` before anything is drawn.
 * Rects come back in VIEWPORT coordinates; subtracting `pageEl`'s own rect
 * (the `.doc-pdf-page` container `PdfHighlightBox`-style overlays already
 * position against) makes them page-relative regardless of scroll position.
 * Empty when the range paints nothing measurable (e.g. mid pdf.js layout).
 */
function quoteOverlaysFromRange(
  keyPrefix: string,
  kind: QuoteOverlay["kind"],
  range: Range,
  pageEl: HTMLElement,
  colorIndex?: number,
  scale = 1,
): QuoteOverlay[] {
  const pageRect = pageEl.getBoundingClientRect()
  const raw = Array.from(range.getClientRects()).map((r) => ({
    left: r.left - pageRect.left,
    top: r.top - pageRect.top,
    right: r.right - pageRect.left,
    bottom: r.bottom - pageRect.top,
    width: r.width,
    height: r.height,
  }))
  // Inflate ~2px horizontally / ~1px vertically so the marker reads as a
  // drawn stroke around the text rather than a tight clip against it.
  return mergeRectsIntoLines(raw, scale).map((r, i) => ({
    key: `${keyPrefix}-${i}`,
    kind,
    colorIndex,
    left: r.left - 2,
    top: r.top - 1,
    width: r.width + 4,
    height: r.height + 2,
  }))
}

export interface QuoteBox {
  key: string
  colorIndex: number
  bbox: { x: number; y: number; w: number; h: number }
  /** The cursor is on this source, drawn stronger, as the painted hot layer is. */
  hot?: boolean
}

export interface SourceRegion {
  key: string // unique per (statement, anchor)
  statementId: string
  label: string // the statement title, the ruler tooltip and the step-1 picker label
  colorIndex: number
  page: number
  /**
   * The passage to find and tint in the text layer, NULL for a proof that has
   * no quotable text (a region: a box on a scanned page). Such a source is
   * PAGE-LEVEL: it rides the ◀▶ walk and the picker like any other, and its
   * click lands on its page, but it is never searched for, never tinted, and
   * never matched by a search chip. Null rather than "" so every consumer has to
   * decide what it does instead of silently matching the empty string.
   */
  quote: string | null
  /**
   * WHERE THE QUOTE SITS ON THE PAGE, in 0..1 page fractions, the backend's own
   * answer, computed at extraction with the engine that read the file.
   *
   * The fallback for a page this browser cannot read. pdf.js returns ZERO characters
   * from a PDF whose fonts carry no usable character map, while PyMuPDF quotes it
   * perfectly, so the text search finds nothing, every source reports "not found",
   * and no highlight paints. The box is what the viewer draws instead. Absent on a
   * source the backend could not place, which is exactly today's behaviour.
   */
  bbox?: { x: number; y: number; w: number; h: number } | null
  /** WHICH copy of `quote` on the page this source means, 1-based. Absent ⇒ the
   *  first. Carried so a span the page repeats verbatim, one identical line per
   *  article paragraph, tints the block it was actually cited from, instead of
   *  every article's evidence landing on the first article's paragraph. */
  occurrence?: number
  summary?: string // the human quote label (the line shown in the source list)
  /** The source-track grouping unit for the current step. Absent ⇒ `statementId`
   *  (step 1: item = statement). Step 3 sets it to a per-statement article key,
   *  and passes the matching `navItems` list. */
  itemKey?: string
}

/**
 * One row of the source-track picker (and the ◀▶ grouping unit). The screen
 * passes the FULL ordered set for the step, including empty groups, which show
 * with a `0` count and are not steppable (honest gaps, never hidden). When
 * `navItems` is absent (step 1) the viewer derives the items from the sources,
 * one per statement.
 */
export interface NavItem {
  key: string // matches SourceRegion.itemKey
  label: string // the picker row label (an article "ID · N", a statement title)
  colorIndex?: number // optional dot colour (step 1 = per-statement; step 3 usually omitted)
}

const itemKeyOf = (s: { itemKey?: string; statementId: string }) =>
  s.itemKey ?? s.statementId

/**
 * DOES THIS SOURCE STILL GET PAINTED, given where the ◀▶ cursor stands?
 *
 * The tint's second narrowing (the first is the selected statement). Both null
 * checks are load-bearing and neither is a defensive habit:
 *
 *  - `curItemKey == null`, the walk is not on an item at all (an empty or
 *    filtered-out set), so there is no cursor to narrow to and nothing narrows.
 *  - `s.itemKey == null`, a source that carries NO per-item key is never
 *    filtered out. Steps 1 and 2 emit statement-scoped sources with no
 *    `itemKey`, and their tint must keep painting the statement's whole set; a
 *    predicate that compared a MISSING key against the cursor's derived one
 *    (`itemKeyOf` falls back to the statementId) would blank those steps.
 *
 * So only sources that explicitly declare which item they belong to narrow,
 * which today is step 3, where one item is one article.
 */
export function tintsForCursor(
  s: { itemKey?: string },
  curItemKey: string | null,
): boolean {
  return curItemKey == null || s.itemKey == null || s.itemKey === curItemKey
}

/** What the jump resolution needs of a source, the rest of a `SourceRegion` has
 *  no say in which source a jump lands on. */
type JumpCandidate = Pick<
  SourceRegion,
  "key" | "statementId" | "page" | "quote" | "occurrence"
>

/**
 * WHICH SOURCE A JUMP LANDS ON, the source the `highlight` names, or null when
 * it names none (a jump to a passage that is nobody's evidence).
 *
 * KEY FIRST. When the highlight already names its source, a card source-line
 * click, `advanceTo`, an `at=` deep link, that IS the answer, with no text to
 * re-derive it from. A key that names no CURRENT source (stale/foreign, the
 * source list changed underneath it) degrades to the tie-break below rather
 * than naming no source: "coarser, never missing", the same contract §6.1's
 * occurrence tie-break already keeps. Absent entirely for the callers that only
 * ever had quote+page+occurrence to give (detections, the issue-brief's
 * fireLead), they fall straight to the tie-break, unaffected.
 *
 * Page and quote text identify the passage; `occurrence` identifies WHICH COPY of
 * it, because a page that repeats a line verbatim, one identical closing line per
 * article paragraph, has several sources with the same page and the same words
 * and only one of them is the proof that was asked for. Without the tie-break the
 * first one won, so the cursor landed on a sibling article's source: the follow-it
 * pin moved the card there, and now the tint narrows there too (`tintsForCursor`
 * rides this cursor). Nothing looks broken when they agree on the wrong article,
 * which is what makes it worth resolving here rather than downstream.
 *
 * The tie-break is tolerant on BOTH sides, for the same reason the tint guard is:
 * a request without an occurrence, or a source that carries none, must resolve
 * exactly as it did before this existed, every caller that never set one is
 * unaffected.
 *
 * Overlapping evidence that survives all of that: the active statement wins.
 */
export function jumpSourceKey(
  sources: JumpCandidate[],
  highlight: Highlight,
  activeStatementId: string | null,
): string | null {
  if (highlight.key != null) {
    const named = sources.find((s) => s.key === highlight.key)
    if (named) return named.key
    // falls through, stale/foreign key degrades to the tie-break below,
    // never to null. Same "coarser, never missing" contract as §6.1.
  }
  const covering = sources.filter(
    (s) =>
      s.page === highlight.page &&
      s.quote === highlight.quote &&
      (highlight.occurrence == null ||
        s.occurrence == null ||
        s.occurrence === highlight.occurrence),
  )
  return (
    (covering.find((s) => s.statementId === activeStatementId) ?? covering[0])
      ?.key ?? null
  )
}

/** Pulse a page container, the visible half of the page-level anchor fallback. */
function flashPage(el: HTMLElement): void {
  el.classList.remove("doc-page-flash")
  void el.offsetWidth // restart the CSS animation
  el.classList.add("doc-page-flash")
  window.setTimeout(() => el.classList.remove("doc-page-flash"), 1300)
}

/**
 * MERGE TABLE-ROW SLIVERS INTO A FEW WIDE LINE BOXES, rendering-only (a
 * marked passage spanning several text runs on one line, a table row's cell
 * boxes, used to paint one `sv2-markbox` sliver per run and a numbered
 * badge on every one of them, "2" repeated eight times across a row).
 *
 * Boxes on the SAME LINE (tops within 2px) that sit ADJACENT in x (the gap
 * between one box's right edge and the next box's left edge is at most 6px,
 * touching or slightly overlapping) collapse into their bounding box.
 *
 * PURE, PIXEL-SPACE geometry: the caller converts a mark's page-fraction
 * boxes to pixels before calling (so the thresholds mean actual screen
 * pixels, independent of zoom/page size) and back after. The raw
 * `PassageMark.boxes` payload sent to the backend is never touched by this,
 * it only changes how many boxes get painted.
 */
export function mergeLineBoxes(boxes: MarkBox[]): MarkBox[] {
  if (boxes.length === 0) return []
  const sorted = [...boxes].sort((a, b) => a.y - b.y || a.x - b.x)
  const merged: MarkBox[] = []
  let current = sorted[0]
  for (let i = 1; i < sorted.length; i++) {
    const b = sorted[i]
    const sameLine = Math.abs(b.y - current.y) <= 2
    const gap = b.x - (current.x + current.w)
    if (sameLine && gap <= 6) {
      const left = Math.min(current.x, b.x)
      const top = Math.min(current.y, b.y)
      const right = Math.max(current.x + current.w, b.x + b.w)
      const bottom = Math.max(current.y + current.h, b.y + b.h)
      current = { x: left, y: top, w: right - left, h: bottom - top }
    } else {
      merged.push(current)
      current = b
    }
  }
  merged.push(current)
  return merged
}

/** `MarkBox` fractions ↔ pixels, against one page's rendered size, so
 *  `mergeLineBoxes`'s 2px/6px thresholds mean real screen pixels. */
function markBoxToPixels(b: MarkBox, pageW: number, pageH: number): MarkBox {
  return { x: b.x * pageW, y: b.y * pageH, w: b.w * pageW, h: b.h * pageH }
}
function markBoxToFraction(b: MarkBox, pageW: number, pageH: number): MarkBox {
  return { x: b.x / pageW, y: b.y / pageH, w: b.w / pageW, h: b.h / pageH }
}

/**
 * The declaration review document viewer, a faithful port of the studio's
 * DocumentViewer (regulatory_cms/src/components/document-viewer). Renders the
 * PDF with pdf.js text layers and paints evidence with the CSS Custom Highlight
 * API (matching quote text against the rendered text layer, the pipeline
 * interface gives quotes, not coordinates).
 *
 * The document is a READ-ONLY highlight surface: its only control is ⌘F (the bar
 * toggle). Nothing on the page is clickable or hoverable. Selection happens on
 * the statement cards; navigation happens in the bar's two independent tracks,
 * ▲▼ is plain find-in-document over the keyword chips, ◀▶ + the picker walk the
 * step's source set (quote by quote, grouped by `navItems`).
 *
 * - `highlight` jumps to the page and highlights the quote (a card source-line
 *   click routes through here, it navigates the source track, never a chip).
 * - `sources` paint the SELECTED statement's evidence as a persistent tint and
 *   feed the ◀▶ source track (grouped by `itemKey` / `navItems`).
 * - The search bar is the shared PowerBar (⌘F handled by the screen). On this
 *   viewer the ▲▼ query means "find in document"; active keyword chips
 *   additionally filter the ◀▶ track.
 */
export function DeclarationDocumentViewer({
  fileUrl,
  highlight,
  sources = [],
  activeStatementId = null,
  open,
  onOpenChange,
  findFocusNonce,
  tokens,
  search,
  onTokensChange,
  onSearchChange,
  placeholder = "Find in document",
  onNavigateStatement,
  onStatementOrder,
  onCurrentSource,
  onAnchorVerification,
  navItems,
  focusFirstOn = null,
  groupOf,
  scale,
  onCurrentPage,
  jumpToPage = null,
  overlayQuotes = false,
  marks = EMPTY_MARKS,
  className = "",
}: {
  fileUrl: string | undefined
  highlight: Highlight | null
  sources?: SourceRegion[]
  /** The screen's selected statement, the viewer tints it and binds the ruler
   *  to it, and syncs the source cursor to it on a card selection. */
  activeStatementId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Forwarded to the bar: a bump focuses the find input even while open. */
  findFocusNonce?: number
  tokens: FilterToken[]
  search: string
  onTokensChange: (tokens: FilterToken[]) => void
  onSearchChange: (s: string) => void
  placeholder?: string
  /** Reported when the ◀▶ walk crosses into another statement's quote (step 1)
   *  so the screen can select + smooth-scroll the matching card on the left. */
  onNavigateStatement?: (statementId: string) => void
  /** Reports distinct statement ids in document reading order, only when that
   *  order actually changes, so the screen can sort its list without loops. */
  onStatementOrder?: (statementIds: string[]) => void
  /** The source track's cursor, reported whenever it moves, the active card
   *  marks the current quote line and shows its position (k/N). */
  onCurrentSource?: (key: string | null) => void
  // Reports, per source key, whether the quote was found in the rendered text
  // layer, the client-side verification signal for the anchor list. The orion
  // backend cannot verify against page text (EvidenceAnchor.verified is false on
  // the wire), so this is the main-app's only honest "found" signal. Only keys
  // whose page has rendered are included (others are "not yet known").
  onAnchorVerification?: (foundByKey: Record<string, boolean>) => void
  /** The step's ordered source-track item list (the ◀▶ grouping + the picker
   *  rows), including empty groups (shown at 0, not steppable). Absent ⇒ the
   *  viewer derives one item per statement from the sources (step 1). */
  navItems?: NavItem[]
  /**
   * A LANDING ORDER: when this token changes (the guided flow addressed a new
   * issue), the walk starts at its FIRST quote, cursor set, document scrolled,
   * hot layer on, never wherever the previous issue left the cursor. The viewer
   * owns the walk's document order, so only it can say which quote is first; it
   * keeps re-asserting the front while that order is still being learned (pages
   * index as they render) and stops the moment the user moves the cursor.
   */
  focusFirstOn?: string | null
  /**
   * GROUP THE PICKER'S ROWS UNDER HEADINGS, display only.
   *
   * Step 1 passes a framework per statement, because a framework is the
   * regulation itself and the level at which a reference-list edition is chosen,
   * so the reader needs to see it somewhere and the document bar is where the
   * statements are already listed. Navigation does not change: the ◀▶ walk is
   * computed from `navigable`, never from these rows, so its order is identical
   * with grouping on or off.
   */
  groupOf?: (itemKey: string) => { key: string; label: string } | null
  /**
   * ZOOM FACTOR, threaded straight to react-pdf's `Page` alongside the existing
   * container-derived `width`, react-pdf multiplies them (`finalWidth = width *
   * scale`), the same combination `PdfViewer`'s zoom already uses. Absent ⇒
   * `scale` defaults to 1 inside react-pdf, which is byte-identical to never
   * passing the prop at all, today's every call site keeps its exact layout.
   */
  scale?: number
  /**
   * THE SCROLL-DERIVED PAGE READOUT for a Studio V2 "page N of M" indicator.
   * Fired from a NEW scroll listener (the component tracked no current page
   * before this), only when the computed page actually changes, never on every
   * scroll tick. Not wired to anything when absent, so it costs nothing on the
   * v1 screens that don't pass it.
   */
  onCurrentPage?: (page: number, total: number) => void
  /**
   * A PAGE-ADDRESSED JUMP, Studio V2's "go to page N" affordance. A narrower
   * sibling of `focusFirstOn`: that one is QUOTE-addressed (it walks to a
   * source's first quote and takes over the ◀▶ cursor), this one only scrolls a
   * page into view and flashes it, touching no source-track state at all. A
   * fresh `token` is a fresh request, same contract as `focusFirstOn` and
   * `WorkbookJump`, set it whenever the caller wants to jump again, even to the
   * same page.
   *
   * IF `highlight` OR `focusFirstOn` ALSO CHANGE ON THE SAME RENDER, THIS JUMP
   * YIELDS TO BOTH: their effects are declared after this one below, so when
   * either fires in the same commit its scroll runs second and is the one left
   * standing. `highlight` owns the quote-level anchor jump, since a card
   * source-line click sets `jumpToPage` and `highlight` together, and the
   * quote's own scroll must win, not the page-level one that only got the
   * right page into the viewport. `focusFirstOn` owns the document's
   * reading-order walk. This is a plain page-level convenience and must never
   * strand either mid-navigation.
   */
  jumpToPage?: { page: number; token: string } | null
  /**
   * STUDIO V2'S "TEXT MARKER" LOOK: continuous, line-merged, rounded overlay
   * rectangles instead of the CSS Custom Highlight API's raw per-text-layer-
   * span painting (which reads as choppy, selection-like word chips). Absent
   * ⇒ every v1 screen paints exactly as before: no overlay divs enter the DOM,
   * doc-anchor/src-cN/src-hot-cN stay named highlights. `doc-search` (the
   * PowerBar find highlight) is UNAFFECTED by this prop in both modes, since a
   * find match looking like a text selection is the natural, expected look.
   */
  overlayQuotes?: boolean
  /**
   * STUDIO V2's PASSAGE MARKS (TUR-1211, W5-1): numbered tinted boxes for the
   * active statement's marked passages, keyed by `mark.page` and rendered
   * inside that page's own element, the same pattern `boxesFor`/`overlaysFor`
   * already use for the fallback highlight / `overlayQuotes` divs. Absent for
   * every v1 screen, which renders none of this (finding 10, codex review
   * wave 5: these used to be positioned against the pane's own body
   * container in page-relative fractions from ONE page's rect, so a mark on
   * page 2+ painted at the wrong spot).
   */
  marks?: PassageMark[]
  className?: string
}) {
  const { translate } = useTranslation()
  const pageEls = useRef(new Map<number, HTMLElement>())
  const indexCache = useRef(new Map<number, TextIndex>())
  const matchCache = useRef(new Map<number, Map<string, Match>>())
  // Bumped whenever a page's text layer renders so highlight effects re-run
  // against the fresh nodes.
  const [readyTick, setReadyTick] = useState(0)

  // `overlayQuotes` mode's three paint layers: one state slice per named
  // highlight they replace (doc-anchor / src-cN / src-hot-cN), kept separate
  // because their three effects run independently and must not clobber each
  // other's pages on every render. Combined into one per-page map below.
  const [anchorOverlays, setAnchorOverlays] =
    useState<Map<number, QuoteOverlay[]>>(EMPTY_OVERLAYS)
  const [srcOverlays, setSrcOverlays] =
    useState<Map<number, QuoteOverlay[]>>(EMPTY_OVERLAYS)
  const [hotOverlays, setHotOverlays] =
    useState<Map<number, QuoteOverlay[]>>(EMPTY_OVERLAYS)

  const registerPage = useCallback((page: number, el: HTMLElement | null) => {
    if (el) pageEls.current.set(page, el)
    else pageEls.current.delete(page)
    indexCache.current.delete(page)
    matchCache.current.delete(page)
  }, [])

  const onPageReady = useCallback((page: number) => {
    indexCache.current.delete(page)
    matchCache.current.delete(page)
    setReadyTick((t) => t + 1)
  }, [])

  const indexFor = useCallback((page: number): TextIndex | null => {
    const cached = indexCache.current.get(page)
    if (cached) return cached
    const el = pageEls.current.get(page)
    if (!el) return null
    const index = buildTextIndex(el)
    indexCache.current.set(page, index)
    return index
  }, [])

  // --- Source regions (persistent tint layer) ---
  const sourcesByPage = useMemo(() => {
    const byPage = new Map<number, SourceRegion[]>()
    for (const s of sources) {
      const list = byPage.get(s.page) ?? []
      list.push(s)
      byPage.set(s.page, list)
    }
    return byPage
  }, [sources])

  useEffect(() => {
    matchCache.current.clear()
  }, [sourcesByPage])

  const matchesFor = useCallback(
    (page: number): Map<string, Match> | null => {
      const cached = matchCache.current.get(page)
      if (cached) return cached
      const index = indexFor(page)
      if (!index) return null
      const matches = new Map<string, Match>()
      for (const s of sourcesByPage.get(page) ?? []) {
        // A quote-less source (a region) has nothing to match in the text layer.
        if (!s.quote) continue
        const m = findQuote(index.text, s.quote, s.occurrence)
        if (m) matches.set(s.key, m)
      }
      matchCache.current.set(page, matches)
      return matches
    },
    [indexFor, sourcesByPage],
  )

  // Find-engine matches (run in the search section below), declared up here
  // because the named-highlight tint layers subtract their intervals: tint never
  // paints beneath a keyword highlight, so the yellow reads on the bare page
  // instead of blending into an unreadable stack. `overlayQuotes` keeps every
  // marker whole instead and fades it while the bar has terms (`searching`
  // below): cut around each keyword, a line-merged marker fell apart into
  // pieces with gaps, which reviewers found harder to read than the overlap.
  const [matches, setMatches] = useState<
    (Match & { page: number; term: string })[]
  >([])
  const tintParts = useCallback(
    (page: number, m: Match): Match[] => {
      if (overlayQuotes) return [m]
      let parts: Match[] = [m]
      for (const x of matches) {
        if (x.page !== page || x.end <= m.start || x.start >= m.end) continue
        const next: Match[] = []
        for (const p of parts) {
          if (x.end <= p.start || x.start >= p.end) {
            next.push(p)
            continue
          }
          if (x.start > p.start) next.push({ start: p.start, end: x.start })
          if (x.end < p.end) next.push({ start: x.end, end: p.end })
        }
        parts = next
      }
      return parts
    },
    [matches, overlayQuotes],
  )

  // --- Client-side anchor verification ---
  // For every source whose page has rendered, report whether its quote was found
  // in that page's text layer. This is the SAME match the highlight layer paints,
  // so a "found" anchor is guaranteed jump-to-highlightable. Sources on
  // not-yet-rendered pages are omitted (unknown until their text layer builds).
  useEffect(() => {
    if (!onAnchorVerification) return
    const found: Record<string, boolean> = {}
    for (const s of sources) {
      const index = indexFor(s.page)
      if (!index) continue // page not rendered yet, leave unknown
      found[s.key] = matchesFor(s.page)?.has(s.key) ?? false
    }
    onAnchorVerification(found)
  }, [sources, readyTick, indexFor, matchesFor, onAnchorVerification])

  const scrollRef = useRef<HTMLDivElement>(null)

  // `overlayQuotes` re-measure signal. The three overlay effects capture pixel
  // rects from `getClientRects()` EAGERLY (unlike the CSS Custom Highlight API,
  // which repaints a live `Range` at browser paint time), and `.doc-pdf-page`
  // carries `content-visibility: auto` for off-screen scroll performance, so a
  // page the effects last measured while off-screen can come back with empty
  // rects that nothing would otherwise ever recompute. A scroll tick is the
  // cheap fix: it costs nothing when `overlayQuotes` is off (the listener is
  // never attached), and re-running the effects on every scroll settle is far
  // cheaper than the layout thrash content-visibility exists to avoid.
  const [overlayTick, setOverlayTick] = useState(0)
  useEffect(() => {
    if (!overlayQuotes) return
    const el = scrollRef.current
    if (!el) return
    // Debounced to SETTLE, not per-frame: a tick reruns all three overlay
    // effects (fresh `getClientRects()` reads across every registered page,
    // fresh per-page Maps), which would defeat `PdfPage`'s memo and reflow
    // every page of a long binder on every scroll frame otherwise, the exact
    // hazard `boxesFor`'s own stable-identity contract exists to avoid.
    let timer = 0
    const onScroll = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setOverlayTick((t) => t + 1), 150)
    }
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      window.clearTimeout(timer)
    }
  }, [overlayQuotes])

  // THE PAGE JUMP (`jumpToPage`): scroll the named page into view and flash it.
  // Touches no source-track state (`curKey`, `onNavigateStatement`), since it
  // is a plain page-level convenience, never a quote-level one. Declared
  // BEFORE both the Anchor highlight effect right below (a card source-line
  // click sets `highlight` and `jumpToPage` in the same commit, and the
  // quote's own scroll must be the one left standing, not this page-level
  // one) and the `focusFirstOn` effect further below, so that when either
  // fires in the same commit, React runs this one first and the other's
  // scroll, called second, is the one left standing (see the prop's doc
  // comment).
  // The token is consumed only once the element is actually found: a jump
  // that names a page which has not registered yet (a deep link into a cold,
  // still-loading document) must retry as pages arrive, not drop silently.
  // `readyTick` is the retry signal, the same role it plays for the honest
  // fallback in the anchor-highlight effect below.
  // `scrollWithoutMovingThePage` (highlights.ts): the native call below also
  // walks every scrollable ancestor up to the document, see its own doc
  // comment for the Windows-only "whole page scrolls" bug this guards.
  const jumpedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!jumpToPage || jumpedFor.current === jumpToPage.token) return
    const el = pageEls.current.get(jumpToPage.page)
    if (!el) return
    jumpedFor.current = jumpToPage.token
    scrollWithoutMovingThePage(() =>
      el.scrollIntoView({ behavior: "smooth", block: "start" }),
    )
    flashPage(el)
  }, [jumpToPage, readyTick])

  // --- Anchor highlight (a card source-line click / a deep link) ---
  // A jump sets `highlight`; the quote it lands on becomes the current source
  // (via the effect below), so ◀▶ continues from it. Quotes that ARE a source
  // region get emphasized in their own colour via the hot layer; with the bar
  // open, a jump also shows the plain yellow doc-anchor highlight so it isn't a
  // silent scroll. Which source it is, including which COPY of a repeated
  // passage, is `jumpSourceKey` above.
  const jumpKey = useMemo(
    () =>
      open && highlight
        ? jumpSourceKey(sources, highlight, activeStatementId)
        : null,
    [open, highlight, sources, activeStatementId],
  )

  const scrolledFor = useRef<Highlight | null>(null)
  useEffect(() => {
    if (!highlight) {
      clearNamedHighlight("doc-anchor")
      if (overlayQuotes) setAnchorOverlays(EMPTY_OVERLAYS)
      scrolledFor.current = null
      return
    }
    const index = indexFor(highlight.page)
    // A quote-less jump (a region) has nothing to match: it goes straight to the
    // page-landing branch below, and, unlike a true misquote, without the
    // flash, because nothing failed. A box simply has no text to find.
    const match =
      index && highlight.quote
        ? findQuote(index.text, highlight.quote, highlight.occurrence)
        : null
    const range = index && match ? rangeFromMatch(index, match) : null
    const pageEl = pageEls.current.get(highlight.page)
    const anchorRange = !jumpKey && range ? range : null
    if (overlayQuotes) {
      // The overlay divs are the paint; the named highlight is cleared instead
      // of populated. Defensive, cheap, and guards against a stale entry from
      // before overlayQuotes was true (e.g. mid dev-time prop toggling).
      clearNamedHighlight("doc-anchor")
      setAnchorOverlays(
        anchorRange && pageEl
          ? new Map([
              [
                highlight.page,
                quoteOverlaysFromRange(
                  "doc-anchor",
                  "anchor",
                  anchorRange,
                  pageEl,
                  undefined,
                  scale ?? 1,
                ),
              ],
            ])
          : EMPTY_OVERLAYS,
      )
    } else {
      setNamedHighlight("doc-anchor", anchorRange ? [anchorRange] : [], 2)
    }
    if (scrolledFor.current === highlight) return
    // "Ready" = the page's real text layer is in the DOM. Before that, indexFor
    // indexes placeholder scraps, so a missing match means nothing.
    const ready = !!pageEl?.querySelector(".react-pdf__Page__textContent")
    if (range) {
      scrolledFor.current = highlight
      scrollRangeIntoView(range)
    } else if (!ready) {
      // Provisional landing: put the cited page in view NOW and RE-ASSERT it on
      // every readyTick, on a long binder the pages above are still growing,
      // which strands any one-shot scroll. The precise jump (or the fallback
      // below) finishes when this page's text layer arrives.
      if (pageEl) {
        scrollWithoutMovingThePage(() =>
          pageEl.scrollIntoView({ behavior: "auto", block: "start" }),
        )
      }
    } else {
      // The honest fallback: the text layer is in and the quote is not on it (a
      // true misquote). Land on the page and say so with a flash, an anchor
      // click must never dead-end. A region carries no quote, so there is no
      // misquote to report: it lands on its page silently.
      scrolledFor.current = highlight
      if (pageEl) {
        scrollElementIntoView(pageEl, "smooth", "start")
        if (highlight.quote) flashPage(pageEl)
      }
    }
  }, [
    highlight,
    jumpKey,
    readyTick,
    scale,
    overlayTick,
    indexFor,
    overlayQuotes,
  ])

  // --- Search + evidence navigation ---
  const [current, setCurrent] = useState(0)
  // The current evidence position, keyed by source KEY (not an index) so filter
  // changes keep the current source whenever it survives the filter.
  const [curKey, setCurKey] = useState<string | null>(null)
  const prevSearchKey = useRef<string | null>(null)

  // The find-in-document query (the ▲▼ track): the text terms only (chips + the
  // live input). This is plain keyword find, identical on every step.
  const searchTerms = useMemo(() => textTerms(tokens, search), [tokens, search])
  const searchKey = searchTerms.join("\0")
  // `overlayQuotes` fades the statement tint while this is on, so the keyword
  // highlights read on top of whole markers (see `tintParts`). The same
  // condition the find track paints under: the bar open and a text term set.
  const searching = open && searchTerms.length > 0

  // A source's in-page position as a 0..1 fraction, the ordering key for the
  // source track (null until the page is indexed).
  const fracFor = useCallback(
    (s: SourceRegion): number | null => {
      const index = indexFor(s.page)
      const m = matchesFor(s.page)?.get(s.key)
      return index && m && index.text.length > 0
        ? m.start / index.text.length
        : null
    },
    [indexFor, matchesFor],
  )

  // Sources in reading order, page, then in-page position (fracFor; end of page
  // until indexed), then insertion order.
  const orderedSources = useMemo(
    () =>
      sources
        .map((s, i) => ({ s, i, at: fracFor(s) ?? Infinity }))
        .sort((a, b) => a.s.page - b.s.page || a.at - b.at || a.i - b.i)
        .map((x) => x.s),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sources, fracFor, readyTick],
  )

  // Report the statements' reading order, only when it actually changes, so the
  // consumer can setState without looping.
  const orderSig = useRef("")
  useEffect(() => {
    const ids: string[] = []
    for (const s of orderedSources)
      if (!ids.includes(s.statementId)) ids.push(s.statementId)
    const sig = ids.join(",")
    if (sig === orderSig.current) return
    orderSig.current = sig
    onStatementOrder?.(ids)
  }, [orderedSources, onStatementOrder])

  // The one link between the two tracks: active keyword chips filter the source
  // track (the ◀▶ walk) to the QUOTES that contain any chip, an extra filter
  // layered on the step's set; it never changes what ▲▼ does. No chips → no
  // filter. A keyword matching nothing yields an empty walk (an honest gap).
  const lc = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase()
  const filteredSources = useMemo(() => {
    const chips = tokens.map((t) => lc(t.term.q))
    if (chips.length === 0) return orderedSources
    // A quote-less source cannot contain a keyword, so a chip filters it out,
    // the same answer the text sources give, arrived at honestly rather than by
    // matching the empty string.
    return orderedSources.filter(
      (s) => !!s.quote && chips.some((q) => lc(s.quote!).includes(q)),
    )
  }, [orderedSources, tokens])

  // The step's source-track items, in order, with counts, and the flat
  // navigable walk. Items with quotes come first, ordered by their earliest
  // quote's document position; empty items (0 count) follow, shown but not
  // steppable (honest gaps). When `navItems` is absent (step 1) the items are
  // derived from the sources, one per statement.
  const { pickerRows, navigable } = useMemo(() => {
    const byItem = new Map<string, SourceRegion[]>()
    for (const s of filteredSources) {
      const k = itemKeyOf(s)
      const list = byItem.get(k)
      if (list) list.push(s)
      else byItem.set(k, [s])
    }
    let rows: {
      key: string
      label: string
      colorIndex?: number
      count: number
    }[]
    let walk: SourceRegion[]
    if (navItems) {
      // `filteredSources` is already in document order, so an item's first
      // appearance there IS its earliest quote's position.
      const firstIdx = new Map<string, number>()
      filteredSources.forEach((s, i) => {
        const k = itemKeyOf(s)
        if (!firstIdx.has(k)) firstIdx.set(k, i)
      })
      const present = navItems
        .filter((n) => byItem.has(n.key))
        .sort((a, b) => firstIdx.get(a.key)! - firstIdx.get(b.key)!)
      const absent = navItems.filter((n) => !byItem.has(n.key))
      rows = [...present, ...absent].map((n) => ({
        key: n.key,
        label: n.label,
        colorIndex: n.colorIndex,
        count: byItem.get(n.key)?.length ?? 0,
      }))
      walk = present.flatMap((n) => byItem.get(n.key)!)
    } else {
      rows = [...byItem.entries()].map(([key, list]) => ({
        key,
        label: list[0].label,
        colorIndex: list[0].colorIndex,
        count: list.length,
      }))
      walk = [...byItem.values()].flat()
    }
    return { pickerRows: rows, navigable: walk }
  }, [filteredSources, navItems])

  const navPos = navigable.findIndex((s) => s.key === curKey)
  const curSource = navPos >= 0 ? navigable[navPos] : null
  const curItemKey = curSource ? itemKeyOf(curSource) : null

  // The persistent source tint paints only while the bar is open, closed, the
  // document stays clean/readable. Tint is SCOPED to the selected statement: only
  // that one statement's quotes are tinted, in its colour, one statement at a
  // time. WITHIN a statement whose sources carry explicit per-item keys (step 3:
  // one item per ARTICLE), the tint narrows again to the ◀▶ cursor's item: the
  // card beside the document shows exactly one article, and tinting its siblings
  // paints quotes that do not belong to what is on screen (a multi-article
  // statement, the TE-style SoC export, made the mismatch visible). The walk,
  // the picker and the ruler still carry the whole set; only the PAINT follows
  // the cursor. The current quote gets a stronger "anchor" tint via the hot layer
  // below (it rides curSource). Keyword matches win over tint, see `tintParts`.
  useEffect(() => {
    const byColor: Range[][] = Array.from(
      { length: SOURCE_COLOR_COUNT },
      () => [],
    )
    const byPage = new Map<number, QuoteOverlay[]>()
    if (open && activeStatementId != null) {
      for (const page of pageEls.current.keys()) {
        const index = indexFor(page)
        const pageMatches = matchesFor(page)
        if (!index || !pageMatches) continue
        const pageEl = overlayQuotes ? pageEls.current.get(page) : undefined
        for (const s of sourcesByPage.get(page) ?? []) {
          if (s.statementId !== activeStatementId) continue
          if (!tintsForCursor(s, curItemKey)) continue
          // In overlay mode the hot layer's own effect (below) paints the
          // cursor's source at full strength; painting it here too would stack
          // two `mix-blend-mode: multiply` divs at the same rect, reading
          // meaningfully darker than the hot layer's specified 45% tint. The
          // named-highlight path never had this problem (CSS priority alone
          // resolves the stack), so the exclusion is overlay-only.
          if (overlayQuotes && open && curSource?.key === s.key) continue
          const m = pageMatches.get(s.key)
          const parts = m ? tintParts(page, m) : []
          parts.forEach((part, partIdx) => {
            const range = rangeFromMatch(index, part)
            if (!range) return
            if (pageEl) {
              const rects = quoteOverlaysFromRange(
                `src-${s.key}-${partIdx}`,
                "src",
                range,
                pageEl,
                s.colorIndex,
                scale ?? 1,
              )
              byPage.set(page, (byPage.get(page) ?? []).concat(rects))
            } else {
              byColor[s.colorIndex % SOURCE_COLOR_COUNT].push(range)
            }
          })
        }
      }
    }
    if (overlayQuotes) {
      setSrcOverlays(byPage)
      for (let i = 0; i < SOURCE_COLOR_COUNT; i++)
        clearNamedHighlight(`src-c${i}`)
    } else {
      byColor.forEach((ranges, i) => setNamedHighlight(`src-c${i}`, ranges, 0))
    }
  }, [
    open,
    activeStatementId,
    curItemKey,
    curSource,
    sourcesByPage,
    readyTick,
    scale,
    overlayTick,
    indexFor,
    matchesFor,
    tintParts,
    overlayQuotes,
  ])

  /** Scroll a source into view by its text range, else its page (never a dead end). */
  const scrollToSource = useCallback(
    (s: SourceRegion, behavior: ScrollBehavior) => {
      const index = indexFor(s.page)
      const m = matchesFor(s.page)?.get(s.key)
      const range = index && m ? rangeFromMatch(index, m) : null
      if (range) {
        scrollRangeIntoView(range, behavior)
        return
      }
      const el = pageEls.current.get(s.page)
      if (el) scrollElementIntoView(el, behavior, "start")
    },
    [indexFor, matchesFor],
  )

  // Make a source current + scroll it into view; the hot layer emphasizes it.
  // The ▲▼ keyword track is deliberately untouched. `report` (default true) tells
  // the screen to select the matching statement when the ◀▶ walk crosses into it
  // (step 1), suppressed (`false`) when WE are reacting to a card selection, so
  // the two never chase each other.
  const selfNavStmt = useRef<string | null>(null)
  const focusKey = useCallback(
    (key: string, report = true, behavior: ScrollBehavior = "smooth") => {
      const s = sources.find((x) => x.key === key)
      if (!s) return
      setCurKey(key)
      scrollToSource(s, behavior)
      if (report && s.statementId !== activeStatementId) {
        selfNavStmt.current = s.statementId
        onNavigateStatement?.(s.statementId)
      }
    },
    [sources, scrollToSource, onNavigateStatement, activeStatementId],
  )

  // ◀▶ / ←→ step through the navigable quotes, wrapping across items.
  const stepNav = useCallback(
    (dir: 1 | -1, behavior: ScrollBehavior = "smooth") => {
      if (navigable.length === 0) return
      const pos = navigable.findIndex((s) => s.key === curKey)
      const next =
        pos < 0
          ? dir > 0
            ? 0
            : navigable.length - 1
          : (pos + dir + navigable.length) % navigable.length
      focusKey(navigable[next].key, true, behavior)
    },
    [navigable, curKey, focusKey],
  )

  // The source cursor, reported upward, the card marks the current quote line
  // and shows its position (the readout half of the source track).
  const lastReportedSource = useRef<string | null>(null)
  useEffect(() => {
    const key = curSource?.key ?? null
    if (key === lastReportedSource.current) return
    lastReportedSource.current = key
    onCurrentSource?.(key)
  }, [curSource, onCurrentSource])

  // When the filter (or a scope/selection change) drops the current source, snap
  // to the first navigable one WITHOUT scrolling, only user actions (◀▶, the
  // picker, a card selection) move the document.
  useEffect(() => {
    if (navigable.length === 0) return
    if (navigable.some((s) => s.key === curKey)) return
    setCurKey(navigable[0].key)
  }, [navigable, curKey])

  // THE LANDING ORDER (`focusFirstOn`): a new token starts the walk at its first
  // quote, scrolled and hot. Document order is still being learned while pages
  // index, so the front of the walk can change under us, keep re-asserting it
  // until the user moves the cursor (◀▶, the picker, a source-line click), which
  // is detected as a cursor we did not place.
  const landing = useRef<{ token: string | null; placed: string | null }>({
    token: null,
    placed: null,
  })
  useEffect(() => {
    if (focusFirstOn == null) return
    if (landing.current.token !== focusFirstOn)
      landing.current = { token: focusFirstOn, placed: null }
    const first = navigable[0]
    if (!first) return
    if (landing.current.placed !== null && curKey !== landing.current.placed)
      return // the user moved on, the landing must not fight them
    if (landing.current.placed === first.key && curKey === first.key) return
    landing.current.placed = first.key
    focusKey(first.key, true, "auto")
  }, [focusFirstOn, navigable, curKey, focusKey])

  // A source-line click (the anchor jump) also becomes the current quote when
  // it's navigable, ◀▶ continues from it. Keyed on the highlight (a fresh object
  // per click), NOT on jumpKey: a bar toggle or source change must never
  // re-assert a long-past jump.
  useEffect(() => {
    if (jumpKey && navigable.some((s) => s.key === jumpKey)) {
      setCurKey(jumpKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight])

  // Selection sync: when the screen selects a different statement, jump to its
  // first navigable quote so its evidence comes into view. A ◀▶ walk that crossed
  // into the statement already scrolled there and reported it (recorded in
  // selfNavStmt), that round trip must NOT re-scroll.
  useEffect(() => {
    if (activeStatementId == null) return
    if (selfNavStmt.current === activeStatementId) {
      selfNavStmt.current = null
      return
    }
    const first = navigable.find((s) => s.statementId === activeStatementId)
    if (first) focusKey(first.key, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatementId])

  // ←/→ step through the navigable quotes while the bar is open and focus isn't
  // in the input. A single quote still navigates, stepping scrolls back to it.
  useEffect(() => {
    if (!open || navigable.length === 0) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      )
        return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Shift+Arrow EXTENDS a text selection — the reviewer is selecting a
      // passage to copy, not stepping the walk. It has to reach the browser
      // untouched, so no preventDefault either (fix P-g).
      if (e.shiftKey) return
      // Key-repeat sweeps: snap instead of smooth so the document keeps up.
      if (e.key === "ArrowRight") {
        e.preventDefault()
        stepNav(1, e.repeat ? "auto" : "smooth")
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        stepNav(-1, e.repeat ? "auto" : "smooth")
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, navigable.length, stepNav])

  // --- Hot layer: the current source glow ---
  // The source cursor gets the stronger "anchor" tint while the bar is open, the
  // only glow left now that the document is read-only (no hover, no peek).
  const hotSet = useMemo(() => {
    const hot = new Set<string>()
    if (open && curSource) hot.add(curSource.key)
    return hot
  }, [open, curSource])

  /**
   * THE SOURCES THIS PAGE COULD NOT PLACE BY TEXT, with the backend's coordinate.
   *
   * Only once the page's text layer is IN (`matchesFor` is null until then), so a
   * box never flashes up on a page whose search is still going to succeed. A source
   * the search did find is not drawn, the painted highlight is the better answer
   * and this is strictly the fallback.
   */
  const boxesByPage = useMemo(() => {
    const byPage = new Map<number, QuoteBox[]>()
    for (const [page, pageSources] of sourcesByPage) {
      const matches = matchesFor(page)
      if (!matches) continue
      const boxes = pageSources.flatMap<QuoteBox>((s) =>
        s.bbox && !matches.has(s.key)
          ? [
              {
                key: s.key,
                colorIndex: s.colorIndex,
                bbox: s.bbox,
                hot: hotSet.has(s.key),
              },
            ]
          : [],
      )
      if (boxes.length) byPage.set(page, boxes)
    }
    return byPage
    // `readyTick` is what makes a page's text layer arriving re-run this: `matchesFor`
    // reads a cache that the tick invalidates, so the map has to be rebuilt then even
    // though the callback identity has not moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesFor, sourcesByPage, hotSet, readyTick])

  // MEMOIZED PER PAGE, NOT REBUILT PER RENDER. `PdfPage` is `memo`-wrapped, so handing
  // it a freshly-built array on every parent render defeats that memo and re-renders
  // every page of a long binder on each keystroke in the search box. Stable identities
  // mean only the pages whose boxes actually changed re-render.
  const boxesFor = useCallback(
    (page: number): QuoteBox[] | undefined => boxesByPage.get(page),
    [boxesByPage],
  )

  /**
   * STUDIO V2'S PASSAGE MARKS, GROUPED PER PAGE (finding 10, codex review
   * wave 5). `captureMarkSelection` already computes each mark's boxes as
   * fractions of ITS OWN page's `getBoundingClientRect()` (`markSelection.ts`),
   * so rendering a mark inside that same page's element, exactly like
   * `boxesFor`'s fallback highlight above, is what makes the fraction
   * correct: the pane used to render every mark as an absolute child of its
   * own body container instead, which is only ever page 1's frame of
   * reference.
   */
  const marksByPage = useMemo(() => {
    if (marks.length === 0) return EMPTY_PAGE_MARKS
    const byPage = new Map<number, PassageMark[]>()
    for (const mark of marks) {
      byPage.set(mark.page, (byPage.get(mark.page) ?? []).concat(mark))
    }
    return byPage
  }, [marks])

  const marksFor = useCallback(
    (page: number): PassageMark[] | undefined => marksByPage.get(page),
    [marksByPage],
  )

  // The three `overlayQuotes` paint layers, combined per page. `overlayQuotes`
  // false ⇒ the three state slices never leave their EMPTY_OVERLAYS identity
  // (the effects above only setState when overlayQuotes is true), so this stays
  // the stable empty map and `PdfPage` renders no overlay divs at all: the
  // "absent prop, byte-identical v1" guarantee.
  const overlaysByPage = useMemo(() => {
    if (!overlayQuotes) return EMPTY_OVERLAYS
    const byPage = new Map<number, QuoteOverlay[]>()
    for (const layer of [anchorOverlays, srcOverlays, hotOverlays]) {
      for (const [page, list] of layer) {
        byPage.set(page, (byPage.get(page) ?? []).concat(list))
      }
    }
    return byPage
  }, [overlayQuotes, anchorOverlays, srcOverlays, hotOverlays])

  const overlaysFor = useCallback(
    (page: number): QuoteOverlay[] | undefined => overlaysByPage.get(page),
    [overlaysByPage],
  )

  useEffect(() => {
    const byColor: Range[][] = Array.from(
      { length: SOURCE_COLOR_COUNT },
      () => [],
    )
    const byPage = new Map<number, QuoteOverlay[]>()
    if (hotSet.size > 0) {
      for (const s of sources) {
        if (!hotSet.has(s.key)) continue
        const index = indexFor(s.page)
        const m = matchesFor(s.page)?.get(s.key)
        if (!index || !m) continue
        const pageEl = overlayQuotes ? pageEls.current.get(s.page) : undefined
        tintParts(s.page, m).forEach((part, partIdx) => {
          const range = rangeFromMatch(index, part)
          if (!range) return
          if (pageEl) {
            const rects = quoteOverlaysFromRange(
              `hot-${s.key}-${partIdx}`,
              "hot",
              range,
              pageEl,
              s.colorIndex,
              scale ?? 1,
            )
            byPage.set(s.page, (byPage.get(s.page) ?? []).concat(rects))
          } else {
            byColor[s.colorIndex % SOURCE_COLOR_COUNT].push(range)
          }
        })
      }
    }
    if (overlayQuotes) {
      setHotOverlays(byPage)
      for (let i = 0; i < SOURCE_COLOR_COUNT; i++)
        clearNamedHighlight(`src-hot-c${i}`)
    } else {
      byColor.forEach((ranges, i) =>
        setNamedHighlight(`src-hot-c${i}`, ranges, 1),
      )
    }
  }, [
    hotSet,
    sources,
    readyTick,
    scale,
    overlayTick,
    indexFor,
    matchesFor,
    tintParts,
    overlayQuotes,
  ])

  // Named highlights live in a global registry, drop ours on unmount.
  useEffect(
    () => () => {
      clearNamedHighlight("doc-anchor")
      clearNamedHighlight("doc-search")
      clearNamedHighlight("doc-search-current")
      for (let i = 0; i < SOURCE_COLOR_COUNT; i++) {
        clearNamedHighlight(`src-c${i}`)
        clearNamedHighlight(`src-hot-c${i}`)
      }
    },
    [],
  )

  // --- Find in document (the ▲▼ track) ---
  // The query is the shared chip expression's text terms. On a linear document
  // AND/OR has no meaning, so we highlight/step matches of ANY term (union).
  useEffect(() => {
    if (!open || searchTerms.length === 0) {
      setMatches([])
      prevSearchKey.current = searchKey
      return
    }
    const all: (Match & { page: number; term: string })[] = []
    for (const page of [...pageEls.current.keys()].sort((a, b) => a - b)) {
      const index = indexFor(page)
      if (!index) continue
      for (const term of searchTerms)
        for (const m of findAllMatches(index.text, term))
          all.push({ ...m, page, term })
    }
    all.sort((a, b) => a.page - b.page || a.start - b.start || a.end - b.end)
    const deduped = all.filter(
      (m, i) =>
        i === 0 || m.page !== all[i - 1].page || m.start !== all[i - 1].start,
    )
    setMatches(deduped)
    if (prevSearchKey.current !== searchKey) {
      // The terms just changed → jump to the FIRST match of the newly-added
      // (last) term, not the document's global first match.
      prevSearchKey.current = searchKey
      const focus = searchTerms[searchTerms.length - 1]
      const idx = deduped.findIndex((m) => m.term === focus)
      setCurrent(idx >= 0 ? idx : 0)
    } else {
      setCurrent((c) => Math.min(c, Math.max(0, deduped.length - 1)))
    }
  }, [open, searchTerms, searchKey, readyTick, indexFor])

  useEffect(() => {
    const others: Range[] = []
    let currentRange: Range | null = null
    matches.forEach((m, i) => {
      const index = indexFor(m.page)
      const range = index ? rangeFromMatch(index, m) : null
      if (!range) return
      if (i === current) currentRange = range
      else others.push(range)
    })
    setNamedHighlight("doc-search", others, 2)
    setNamedHighlight(
      "doc-search-current",
      currentRange ? [currentRange] : [],
      3,
    )
    // ▲▼ is find-in-document: the current match always scrolls into view.
    if (currentRange) scrollRangeIntoView(currentRange)
  }, [matches, current, indexFor])

  const step = (dir: 1 | -1) =>
    setCurrent((c) =>
      matches.length ? (c + dir + matches.length) % matches.length : 0,
    )

  // --- Overview ruler: the SELECTED statement's quote positions, promoted to
  // the document's right edge, drawn in its single colour; orientation-only (no
  // scrub). A tick per quote at its document-relative position (page top +
  // in-page offset fraction). Recomputed on readyTick + container resizes.
  const [layoutTick, setLayoutTick] = useState(0)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setLayoutTick((t) => t + 1))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const activeColorIndex = useMemo(
    () =>
      orderedSources.find((s) => s.statementId === activeStatementId)
        ?.colorIndex ?? 0,
    [orderedSources, activeStatementId],
  )
  const rulerTicks = useMemo(() => {
    if (!open || activeStatementId == null) return []
    const scroll = scrollRef.current
    if (!scroll || scroll.scrollHeight <= 0) return []
    const scrollRect = scroll.getBoundingClientRect()
    const ticks: { key: string; y: number }[] = []
    for (const s of orderedSources) {
      if (s.statementId !== activeStatementId) continue
      const el = pageEls.current.get(s.page)
      if (!el) continue
      const rect = el.getBoundingClientRect()
      const top = rect.top - scrollRect.top + scroll.scrollTop
      const frac = fracFor(s) ?? 0.5
      ticks.push({
        key: s.key,
        y: (top + frac * rect.height) / scroll.scrollHeight,
      })
    }
    return ticks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeStatementId, orderedSources, readyTick, layoutTick, fracFor])

  // THE CURRENT-PAGE READOUT (`onCurrentPage`): no page-tracking scroll listener
  // existed before this, the viewer scrolled by RANGE (a source's text), never
  // by page, so nothing needed one. Attached only while a consumer is listening,
  // so a v1 screen that never passes `onCurrentPage` pays nothing for it.
  // "Current" = the LAST page whose top has crossed a threshold a third of the
  // way down the viewport, the same rule a reader's eye follows: a page that
  // has just started peeking in at the bottom is not yet what's being read.
  //
  // The dedupe key lives in a REF, not a local inside the effect: `readyTick`
  // is a dependency (a page finishing its text layer can shift the layout
  // enough to change the current page with no scroll event at all), so the
  // effect itself tears down and re-creates on every page load, a local would
  // reset to "nothing reported yet" on each of those and re-fire the SAME
  // page, which is exactly the every-tick noise this prop promises not to be.
  // Keyed on page AND total: a reader parked on page 1 while more pages
  // register must still see the total climb (1 of 3 → 1 of 12).
  const reportedPage = useRef<string>("")
  useEffect(() => {
    if (!onCurrentPage) return
    const container = scrollRef.current
    if (!container) return
    const report = () => {
      const total = pageEls.current.size
      if (total === 0) return
      const threshold =
        container.getBoundingClientRect().top +
        container.getBoundingClientRect().height / 3
      let current = 1
      for (const page of [...pageEls.current.keys()].sort((a, b) => a - b)) {
        const el = pageEls.current.get(page)
        if (!el || el.getBoundingClientRect().top > threshold) break
        current = page
      }
      const sig = `${current}/${total}`
      if (sig === reportedPage.current) return
      reportedPage.current = sig
      onCurrentPage(current, total)
    }
    report()
    container.addEventListener("scroll", report, { passive: true })
    return () => container.removeEventListener("scroll", report)
  }, [onCurrentPage, readyTick])

  return (
    <div
      className={cn(
        "doc-viewer",
        overlayQuotes && searching && "doc-viewer-searching",
        className,
      )}
    >
      <div className="doc-scroll" ref={scrollRef}>
        <PdfPages
          fileUrl={fileUrl}
          registerPage={registerPage}
          boxesFor={boxesFor}
          overlaysFor={overlaysFor}
          marksFor={marksFor}
          onPageReady={onPageReady}
          scale={scale}
        />
      </div>

      {/* Overview ruler: where the selected statement's evidence lives, at a
          glance. Orientation-only, not a control (layoutTick keeps it aligned). */}
      {rulerTicks.length > 1 && (
        <div className="doc-ruler" aria-hidden="true">
          {rulerTicks.map((t) => (
            <span
              key={t.key}
              className={`doc-ruler-tick${t.key === curKey ? "current" : ""}`}
              style={{
                top: `${t.y * 100}%`,
                background: sourceColorVar(activeColorIndex),
              }}
            />
          ))}
        </div>
      )}

      <PowerBar
        open={open}
        onToggle={() => onOpenChange(!open)}
        focusNonce={findFocusNonce}
        tokens={tokens}
        activeTerm={matches[current]?.term ?? null}
        search={search}
        onTokensChange={onTokensChange}
        onSearchChange={onSearchChange}
        placeholder={placeholder}
        extra={
          <>
            {searchTerms.length > 0 && (
              <span className="doc-bar-count doc-bar-count-fixed">
                {matches.length ? current + 1 : 0}/{matches.length}
              </span>
            )}
            <button
              className="doc-bar-btn"
              onClick={() => step(-1)}
              disabled={matches.length === 0}
              aria-label={translate("MC_DECL_DOC_PREV_MATCH")}
            >
              <ChevronUp size={13} />
            </button>
            <button
              className="doc-bar-btn"
              onClick={() => step(1)}
              disabled={matches.length === 0}
              aria-label={translate("MC_DECL_DOC_NEXT_MATCH")}
            >
              <ChevronDown size={13} />
            </button>
            {pickerRows.length > 0 && (
              <>
                <span className="doc-bar-divider" />
                <HoldStepButton
                  dir={-1}
                  disabled={navigable.length === 0}
                  label={translate("MC_DECL_DOC_PREV_QUOTE")}
                  holdTitle={translate("MC_DECL_DOC_HOLD_TITLE", {
                    label: translate("MC_DECL_DOC_PREV_QUOTE"),
                  })}
                  onStep={stepNav}
                >
                  <ChevronLeft size={14} />
                </HoldStepButton>
                <span className="doc-bar-count doc-bar-count-fixed">
                  {navigable.length ? Math.max(navPos, 0) + 1 : 0}/
                  {navigable.length}
                </span>
                <HoldStepButton
                  dir={1}
                  disabled={navigable.length === 0}
                  label={translate("MC_DECL_DOC_NEXT_QUOTE")}
                  holdTitle={translate("MC_DECL_DOC_HOLD_TITLE", {
                    label: translate("MC_DECL_DOC_NEXT_QUOTE"),
                  })}
                  onStep={stepNav}
                >
                  <ChevronRight size={14} />
                </HoldStepButton>
                <BarItemPicker
                  items={pickerRows}
                  groupOf={groupOf}
                  currentKey={curItemKey}
                  jumpTitle={(label) =>
                    translate("MC_DECL_DOC_PICKER_JUMP", { label })
                  }
                  onSelect={(key) => {
                    const first = navigable.find((s) => itemKeyOf(s) === key)
                    if (first) focusKey(first.key)
                  }}
                />
              </>
            )}
          </>
        }
      />
    </div>
  )
}

/** Renders the PDF pages inside .doc-scroll: measures width, registers each page
 *  container + reports text-layer readiness for the highlight engine. */
function PdfPages({
  fileUrl,
  registerPage,
  onPageReady,
  boxesFor,
  overlaysFor,
  marksFor,
  scale,
}: {
  fileUrl: string | undefined
  registerPage: (page: number, el: HTMLElement | null) => void
  onPageReady: (page: number) => void
  /** Undefined for a page with no fallback boxes, `PdfPage` is memo-wrapped, so an
   *  empty array literal here would be a fresh identity on every render. */
  boxesFor: (page: number) => QuoteBox[] | undefined
  /** Same identity-stability contract as `boxesFor`, for the `overlayQuotes`
   *  "text marker" divs. Undefined for a page with none, including every
   *  page when `overlayQuotes` is off, so `PdfPage` renders nothing new. */
  overlaysFor: (page: number) => QuoteOverlay[] | undefined
  /** Same identity-stability contract as `boxesFor`, for Studio V2's passage
   *  marks. Undefined for a page with none, including every page when the
   *  caller never passes `marks` (every v1 screen). */
  marksFor: (page: number) => PassageMark[] | undefined
  /** See `DeclarationDocumentViewer`'s `scale` prop, passed straight through. */
  scale?: number
}) {
  const { translate } = useTranslation()
  const [numPages, setNumPages] = useState(0)
  const [width, setWidth] = useState<number | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.floor(entry.contentRect.width))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={wrapRef}>
      <Document
        file={fileUrl}
        options={PDF_OPTIONS}
        onLoadSuccess={(doc) => setNumPages(doc.numPages)}
        loading={<DocPageSkeleton />}
        error={
          <div className="caption muted">
            {translate("MC_DECL_DOC_LOAD_ERROR")}
          </div>
        }
      >
        {width !== null &&
          Array.from({ length: numPages }, (_, i) => (
            <PdfPage
              key={i + 1}
              pageNumber={i + 1}
              width={width}
              scale={scale}
              registerPage={registerPage}
              onPageReady={onPageReady}
              boxes={boxesFor(i + 1)}
              overlays={overlaysFor(i + 1)}
              marks={marksFor(i + 1)}
            />
          ))}
      </Document>
    </div>
  )
}

// Identity-stable: a fresh options object would make react-pdf reload the doc.
const PDF_OPTIONS = { standardFontDataUrl: "/standard_fonts/" }

/** Placeholder sheet shown while the PDF document itself is loading. */
function DocPageSkeleton() {
  const line = (w: string, extra?: React.CSSProperties) => (
    <span className="skeleton" style={{ width: w, ...extra }} />
  )
  return (
    <div className="doc-skeleton-page">
      {line("30%", { height: "2%" })}
      {line("42%")}
      <span style={{ flexShrink: 0, height: "3%" }} />
      {line("56%", { height: "2.4%" })}
      <span style={{ flexShrink: 0, height: "1%" }} />
      {line("100%")}
      {line("97%")}
      {line("99%")}
      {line("95%")}
      {line("62%")}
      <span style={{ flexShrink: 0, height: "2%" }} />
      {line("100%")}
      {line("96%")}
      {line("44%")}
    </div>
  )
}

const PdfPage = memo(function PdfPage({
  pageNumber,
  width,
  scale,
  registerPage,
  onPageReady,
  boxes,
  overlays,
  marks,
}: {
  pageNumber: number
  width: number
  /** See `DeclarationDocumentViewer`'s `scale` prop, passed straight through. */
  scale?: number
  registerPage: (page: number, el: HTMLElement | null) => void
  onPageReady: (page: number) => void
  /** Sources on this page the text layer could not place, drawn as boxes. */
  boxes?: QuoteBox[]
  /** `overlayQuotes` mode's "text marker" divs for this page: empty/undefined
   *  whenever the prop is off. */
  overlays?: QuoteOverlay[]
  /** This page's Studio V2 passage marks, empty/undefined when none/absent. */
  marks?: PassageMark[]
}) {
  const { translate } = useTranslation()
  const [ratio, setRatio] = useState<number | null>(null)
  const handleRef = useCallback(
    (el: HTMLDivElement | null) => registerPage(pageNumber, el),
    [registerPage, pageNumber],
  )
  // The DISPLAYED ratio, not `p.height / p.width`: react-pdf reports the
  // unrotated box's x1/y1 but draws the page at its `/Rotate` and from its
  // side lengths (`pageAspectRatio`).
  const handleLoad = useCallback(
    (p: {
      width: number
      height: number
      rotate?: number
      view?: readonly number[]
    }) => setRatio((r) => r ?? pageAspectRatio(p)),
    [],
  )
  const handleTextLayer = useCallback(
    () => onPageReady(pageNumber),
    [onPageReady, pageNumber],
  )
  const handleTextLayerError = useCallback(
    (e: Error) => {
      // eslint-disable-next-line no-console
      console.error(`text layer failed for page ${pageNumber}:`, e)
      // A PAGE WITH NO TEXT LAYER IS SETTLED, NOT PENDING, and it is exactly the page
      // the box fallback exists for. Only SUCCESS used to mark a page ready, so on a
      // scan the tick never came, `matchesFor` stayed null, and `boxesByPage` skipped
      // the page forever: the box arrived, was correct, and was never drawn. The
      // fallback was gated behind the one thing that fails on the documents it is for.
      onPageReady(pageNumber)
    },
    [onPageReady, pageNumber],
  )

  // Once a page has loaded we know its exact height; before that fall back to an
  // A4 portrait estimate. Either way the height feeds contain-intrinsic-size so
  // content-visibility can skip layout/paint for off-screen pages without the
  // scrollbar jumping. Every page still MOUNTS (react-pdf builds the text layer),
  // so the highlight/search index stays available for the whole document.
  //
  // `renderedWidth` mirrors react-pdf's own math (`Page.js`: passing both `width`
  // and `scale` renders at `width * scale`) so our wrapper's box, the fallback
  // highlight overlay's positioning context, always matches the canvas it
  // wraps. `scale` undefined ⇒ `renderedWidth === width`, the pre-existing size.
  const renderedWidth = width * (scale ?? 1)
  const pageHeight = Math.round(renderedWidth * (ratio ?? 1.414))

  return (
    <div className="doc-page-block" style={{ marginBottom: 16 }}>
      {/* The page label was hardcoded English in a German-first product; the key
          it should have used has existed all along. Studio V2 restyles this line
          into the design's centred caption below the page via scoped rules
          (`studioV2.css`, `.sv2-pane-doc .doc-page-block/.doc-page-label`) rather
          than reordering it here, so v1's screens keep the label exactly where
          they have always had it. The two class names exist only as hooks for
          that scoping. */}
      <div className="micro-label doc-page-label" style={{ marginBottom: 6 }}>
        {translate("PDF_VIEWER/PAGE_PLACEHOLDER", { pageNumber })}
      </div>
      <div
        className="doc-pdf-page"
        // Named the same way react-pdf's own inner `.react-pdf__Page` div
        // already is, so a mark box rendered as a plain sibling of `<Page>`
        // below still lands INSIDE an element the page number identifies,
        // the contract finding 10's fix (and its test) relies on.
        data-page-number={pageNumber}
        style={{
          // Explicit width so a zoomed page (width * scale) overflows the
          // scroll container horizontally instead of being clipped by the
          // block's own overflow: hidden at the pane width.
          width: renderedWidth,
          height: ratio ? pageHeight : undefined,
          contentVisibility: "auto",
          containIntrinsicSize: `${renderedWidth}px ${pageHeight}px`,
        }}
        ref={handleRef}
      >
        <Page
          pageNumber={pageNumber}
          width={width}
          scale={scale}
          renderAnnotationLayer={false}
          onLoadSuccess={handleLoad}
          onRenderTextLayerSuccess={handleTextLayer}
          onRenderTextLayerError={handleTextLayerError}
        />
        {/* THE FALLBACK HIGHLIGHT. Positioned in page fractions, so it does not care
            what width the page is rendered at and needs no re-measure on resize. It
            is drawn ONLY where the text search already failed, so a page the browser
            reads normally is untouched, same paint, same colours, as before. */}
        {boxes?.map((b) => (
          <div
            key={b.key}
            data-quote-box={b.key}
            className={cn("doc-quote-box", b.hot && "hot")}
            style={
              {
                left: `${b.bbox.x * 100}%`,
                top: `${b.bbox.y * 100}%`,
                width: `${b.bbox.w * 100}%`,
                height: `${b.bbox.h * 100}%`,
                // The source's own colour, so a box and a painted quote read as the
                // same track rather than as two different kinds of thing.
                "--quote-c": sourceColorVar(b.colorIndex),
              } as CSSProperties
            }
          />
        ))}
        {/* STUDIO V2'S "TEXT MARKER" OVERLAY (`overlayQuotes`): continuous,
            line-merged, rounded rects in PIXELS against this page, replacing
            the CSS Custom Highlight API's choppy per-span painting for
            doc-anchor/src-cN/src-hot-cN. Absent whenever the prop is off, so
            v1 renders none of this. */}
        {overlays?.map((o) => (
          <div
            key={o.key}
            data-quote-overlay={o.key}
            className={cn(
              "doc-quote-overlay",
              o.kind === "anchor" && "doc-quote-overlay-anchor",
              o.kind === "hot" && "doc-quote-overlay-hot",
              o.kind === "src" && "doc-quote-overlay-src",
            )}
            style={
              {
                left: o.left,
                top: o.top,
                width: o.width,
                height: o.height,
                ...(o.colorIndex !== undefined
                  ? { "--quote-c": sourceColorVar(o.colorIndex) }
                  : {}),
              } as CSSProperties
            }
          />
        ))}
        {/* STUDIO V2'S PASSAGE MARKS (TUR-1211, W5-1): numbered tinted boxes,
            in the same page-fraction convention as the fallback highlight
            above, rendered inside THIS page's own element (finding 10, codex
            review wave 5), the same container `captureMarkSelection` measured
            its fractions against, so a mark on any page lands where it was
            drawn, not just page 1.

            A passage spanning several text runs on one line (a table row's
            cells) captures one box per run; `mergeLineBoxes` collapses the
            ones that share a line into a few wide boxes instead of many
            slivers (pixel-space thresholds, so the fraction boxes are
            converted to this page's pixels and back). Every resulting box
            gets the tint; only the FIRST one in reading order (smallest y,
            then smallest x) gets the numbered badge, so "n" appears once per
            mark, not once per run. */}
        {marks?.map((mark) => {
          const mergedPx = mergeLineBoxes(
            mark.boxes.map((b) =>
              markBoxToPixels(b, renderedWidth, pageHeight),
            ),
          )
          const merged = mergedPx.map((b) =>
            markBoxToFraction(b, renderedWidth, pageHeight),
          )
          const firstIndex = merged.reduce(
            (best, b, i) =>
              best < 0 ||
              b.y < merged[best].y ||
              (b.y === merged[best].y && b.x < merged[best].x)
                ? i
                : best,
            -1,
          )
          return merged.map((box, i) => (
            <div
              key={`${mark.n}-${i}`}
              data-testid="mark-box"
              className="sv2-markbox"
              data-mark-page={mark.page}
              style={{
                left: `${box.x * 100}%`,
                top: `${box.y * 100}%`,
                width: `${box.w * 100}%`,
                height: `${box.h * 100}%`,
              }}
            >
              {i === firstIndex && (
                <span
                  className="sv2-markbadge"
                  aria-label={translate("MC_DECL_V2_MARK_BADGE_LABEL", {
                    n: mark.n,
                  })}
                >
                  <span aria-hidden="true">{mark.n}</span>
                </span>
              )}
            </div>
          ))
        })}
      </div>
    </div>
  )
})

/**
 * A source-track step button with hold-to-sweep, the same idiom as the
 * PowerBar's chip-strip nudges: a click steps once (smooth), holding past the
 * delay auto-advances with acceleration, snap-scrolling each landing so rapid
 * stepping stays fluid. Releasing before the delay is the click.
 */
function HoldStepButton({
  dir,
  disabled,
  label,
  holdTitle,
  onStep,
  children,
}: {
  dir: 1 | -1
  disabled: boolean
  label: string
  holdTitle: string
  onStep: (dir: 1 | -1, behavior?: ScrollBehavior) => void
  children: React.ReactNode
}) {
  const HOLD_DELAY_MS = 350
  const state = useRef<{
    timer: number
    engaged: boolean
    delay: number
  } | null>(null)
  const start = (e: React.PointerEvent) => {
    if (e.button !== 0 || disabled) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    const s = { timer: 0, engaged: false, delay: 240 }
    state.current = s
    const tick = () => {
      s.engaged = true
      onStep(dir, "auto")
      s.delay = Math.max(70, s.delay * 0.85) // accelerate toward ~14 steps/s
      s.timer = window.setTimeout(tick, s.delay)
    }
    s.timer = window.setTimeout(tick, HOLD_DELAY_MS)
  }
  const end = (commit: boolean) => () => {
    const s = state.current
    state.current = null
    if (!s) return
    clearTimeout(s.timer)
    if (commit && !s.engaged) onStep(dir)
  }
  // Unmount mid-hold must not leave the timer chain running.
  useEffect(
    () => () => {
      if (state.current) clearTimeout(state.current.timer)
    },
    [],
  )
  return (
    <button
      className="doc-bar-btn"
      disabled={disabled}
      aria-label={label}
      title={holdTitle}
      onPointerDown={start}
      onPointerUp={end(true)}
      onPointerCancel={end(false)}
    >
      {children}
    </button>
  )
}

/**
 * The source-track item picker in the bar: click it to jump the ◀▶ cursor to an
 * item's first quote. Rows are the step's items (statements on step 1, articles
 * on step 3), each with its quote count. An empty item shows a `0` and is
 * disabled, shown, never hidden (an honest gap). The menu opens upward because
 * the bar is docked at the bottom.
 */
/** One picker row, as the bar renders it. */
export interface PickerRow {
  key: string
  label: string
  colorIndex?: number
  count: number
}

/**
 * CHUNK THE PICKER'S ROWS UNDER THEIR HEADINGS, IN THE ORDER THEY ARRIVED.
 *
 * A group takes the position of its FIRST row, and rows keep their relative order
 * inside it, so the row sequence handed in is preserved exactly, only re-chunked.
 * With no `groupOf` the result is one unlabelled group holding every row, which
 * renders identically to the flat list that preceded grouping.
 *
 * It returns ROWS AND NOTHING ELSE. The ◀▶ walk is a separate array the viewer
 * builds from the sources themselves and never from these, which is what makes the
 * walk order provably unchanged by grouping: there is no path from here to it.
 */
export function groupPickerRows(
  items: PickerRow[],
  groupOf?: (itemKey: string) => { key: string; label: string } | null,
): { key: string; label: string | null; items: PickerRow[] }[] {
  const groups: { key: string; label: string | null; items: PickerRow[] }[] = []
  const at = new Map<string, number>()
  for (const item of items) {
    const g = groupOf?.(item.key) ?? null
    const key = g?.key ?? ""
    let i = at.get(key)
    if (i === undefined) {
      i = groups.length
      at.set(key, i)
      groups.push({ key, label: g?.label ?? null, items: [] })
    }
    groups[i].items.push(item)
  }
  return groups
}

/**
 * THE PICKER, OPTIONALLY GROUPED, and the grouping is DISPLAY ONLY.
 *
 * `groupOf` puts a heading above the rows that share a group (step 1 groups its
 * statements under their framework: a framework is the regulation itself, and it
 * is the level at which a reference-list edition is chosen, so the reader needs to
 * see it). Groups appear in order of their first row, and rows keep their relative
 * order inside one, so the ROW SEQUENCE this component receives is preserved
 * exactly, only re-chunked under headings.
 *
 * IT DOES NOT TOUCH THE WALK. `navigable` is computed separately by the caller and
 * never derived from these rows; the ◀▶ order is byte-identical with grouping on
 * or off. That separation is the whole point: "the grouping is what the user reads,
 * not a new way to move".
 */
function BarItemPicker({
  items,
  currentKey,
  jumpTitle,
  onSelect,
  groupOf,
}: {
  items: { key: string; label: string; colorIndex?: number; count: number }[]
  currentKey: string | null
  jumpTitle: (label: string) => string
  onSelect: (key: string) => void
  /** A row's group heading, or null for an ungrouped row. Absent ⇒ a flat list,
   *  which is exactly what every step but the first renders. */
  groupOf?: (itemKey: string) => { key: string; label: string } | null
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener("click", onClick)
    return () => window.removeEventListener("click", onClick)
  }, [open])

  const cur =
    items.find((s) => s.key === currentKey) ??
    items.find((s) => s.count > 0) ??
    items[0] ??
    null

  const groups = groupPickerRows(items, groupOf)

  if (!cur) return null

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 0 }}>
      <button
        className="doc-bar-stmt"
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={jumpTitle(cur.label)}
      >
        {cur.colorIndex !== undefined && (
          <span
            className="src-dot"
            style={{ background: sourceColorVar(cur.colorIndex) }}
          />
        )}
        <span className="doc-bar-stmt-label">{cur.label}</span>
        <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.7 }} />
      </button>
      {open && (
        <div className="doc-bar-stmt-menu">
          {groups.map((g) => (
            <div key={g.key}>
              {g.label !== null && (
                <div className="doc-bar-stmt-group">{g.label}</div>
              )}
              {g.items.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  disabled={s.count === 0}
                  className={cn(
                    "doc-bar-stmt-opt",
                    s.key === currentKey && "current",
                  )}
                  onClick={() => {
                    onSelect(s.key)
                    setOpen(false)
                  }}
                >
                  {s.colorIndex !== undefined && (
                    <span
                      className="src-dot"
                      style={{ background: sourceColorVar(s.colorIndex) }}
                    />
                  )}
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.label}
                  </span>
                  <span className="doc-bar-stmt-count">{s.count}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
